import React, { ReactNode, useEffect, useRef } from 'react';

export enum InputType {
    DATE = 'date',
    EMAIL = 'email',
    NUMBER = 'number',
    PASSWORD = 'password',
    SEARCH = 'search',
    TEL = 'tel',
    CELLULAR = 'cellular',
    TEXT = 'text'
}

export enum InputCustomType {
    CELLULAR = 'cellular',
    DAYDATE = 'daydate',
    LONGDATE = 'longdate',
    MONEY = 'money',
    PC = 'pc',
    PERCENTAGE = 'percentage',
    SEARCH = 'search',
    SHORTDATE = 'shortdate',
    SIN = 'sin',
    TAN = 'tan',
    TEL = 'tel'
}

export enum InputMode {
    DECIMAL = 'decimal',
    EMAIL = 'email',
    NUMERIC = 'numeric',
    SEARCH = 'search',
    TEL = 'tel',
    TEXT = 'text',
    URL = 'url',
    NONE = 'none'
}

export enum InputPrefix {
    CELLULAR = 'cellular',
    DATE = 'date',
    DOLLAR = '$',
    SEARCH = 'search',
    TEL = 'tel'
}

export enum InputSuffix {
    DOLLAR = '$',
    PERCENT = '%'
}

export enum AutoCompleteType {
    ON = 'on',
    OFF = 'off',
    NAME = 'name'
    // La liste continue mais elle est trop grosse pour tous les ajouter ici
    // donc mieux d'ajouter au fur et à mesure au besoin
}

type Decimal = 0 | 2 | 3;

type MaxDecimalLength = 0 | 1 | 2 | 3;

interface IInputProps {
    // slots
    label?: string;
    errorLabel?: string;
    hint?: string | ReactNode;
    labelToolTip?: string;

    // attributes
    shouldAutoComplete?: AutoCompleteType;
    customType?: InputCustomType;
    isDark?: boolean;
    decimals?: Decimal;
    isDisabled?: boolean;
    isError?: boolean;
    hideRequiredLabelling?: boolean;
    identifier?: string;
    inputPrefix?: InputPrefix;
    inputSuffix?: InputSuffix;
    inputWidth?: number;
    inputMode?: InputMode;
    max?: number; // utilise pour type="number" ou custom-type="money"
    maxDecimalLength?: MaxDecimalLength;
    maxLength?: number;
    min?: number; // utilise pour type="number" ou custom-type="money"
    minLength?: number;
    name?: string;
    pattern?: string;
    quietValidate?: boolean;
    isReadOnly?: boolean;
    isRequired?: boolean;
    shouldSpellCheck?: boolean;
    type?: InputType;
    value?: string;

    // events
    onChange?: (value: string) => void;
    onFocusOut?: (value: string) => void;
    onFocus?: () => void;

    // extra de nous
    inputRef?: any;
    dataTestId?: string;
}

export const Input: React.FC<IInputProps> = ({
    // slots
    label,
    errorLabel,
    hint,
    labelToolTip,
    // attributes
    shouldAutoComplete,
    customType,
    isDark,
    decimals,
    isDisabled = false,
    isError,
    hideRequiredLabelling = false,
    identifier,
    inputPrefix,
    inputSuffix,
    inputWidth,
    inputMode,
    max,
    maxDecimalLength = 2,
    maxLength,
    min,
    minLength,
    name = 'input-01',
    pattern,
    quietValidate = false,
    isReadOnly = false,
    isRequired = true,
    shouldSpellCheck = true,
    type,
    value,
    // events
    onChange,
    onFocus,
    onFocusOut,
    // extra de nous
    inputRef,
    dataTestId
}) => {
    const ref = useRef(null);

    const handleChange = (e: any) => {
        onChange(e.target.value);
    };

    const handleFocusOut = (e: any) => {
        onFocusOut(e.target.value);
    };

    useEffect(() => {
        ref.current.addEventListener('dsdInputChange', (e: any) => {
            if (onChange) handleChange(e);
        });

        ref.current.addEventListener('dsdInputFocusout', (e: any) => {
            if (onFocusOut) handleFocusOut(e);
        });
    }, []);

    return (
        <div ref={inputRef} onFocus={onFocus}>
            {/* @ts-ignore */}
            <dsd-input
                ref={ref}
                data-testid={dataTestId}
                autocomplete={shouldAutoComplete}
                custom-type={customType}
                dark={isDark}
                decimals={decimals}
                disabled={isDisabled}
                error={isError}
                hide-required-labelling={hideRequiredLabelling}
                identifier={identifier}
                input-prefix={inputPrefix}
                input-suffix={inputSuffix}
                input-width={inputWidth}
                input-mode={inputMode}
                max={max}
                max-decimal-length={maxDecimalLength}
                maxlength={maxLength}
                min={min}
                minlength={minLength}
                name={name}
                pattern={pattern}
                quiet-validate={quietValidate}
                readonly={isReadOnly}
                required={isRequired}
                spellcheck={shouldSpellCheck}
                type={type}
                value={value}
            >
                <span slot="label">{label}</span>
                <span slot="label-tooltip">{labelToolTip}</span>
                <span slot="hint">{hint}</span>
                <span slot="error">{errorLabel}</span>
                {/* @ts-ignore */}
            </dsd-input>
        </div>
    );
};
